import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { cpfValido, mascararCpf, soDigitos } from '../utils/cpf';

/**
 * Autocadastro por link (/cadastro/:token).
 *
 * A coordenação manda o link para secretários, supervisores e diretores. A
 * pessoa preenche os próprios dados e a senha; o acesso só vale depois que
 * a coordenação aprovar. Perfil e município vêm do link.
 *
 * O CPF é digitado duas vezes: é ele que liga a pessoa ao RH e aos outros
 * acessos dela, e um número trocado só seria percebido tarde demais.
 */

const VAZIO = {
  first_name: '', last_name: '', cpf: '', cpf_confirmacao: '', email: '', phone: '',
  school: '', password: '', password_confirm: '',
};

// DRF devolve {campo: ["msg"]} ou {error: "msg"}.
const primeiraMensagem = (valor) => (Array.isArray(valor) ? valor[0] : valor);

function Autocadastro() {
  const { token } = useParams();
  const [darkMode, setDarkMode] = useState(true);
  const [link, setLink] = useState(null);
  const [erroLink, setErroLink] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState(VAZIO);
  const [erros, setErros] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/autocadastro/publico/${token}/`);
        const dados = await resp.json().catch(() => ({}));
        if (!vivo) return;
        if (resp.ok) setLink(dados);
        else setErroLink(dados.error || 'Link de cadastro inválido.');
      } catch {
        if (vivo) setErroLink('Não foi possível abrir o cadastro. Verifique sua internet e tente de novo.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const mudar = (campo, valor) => {
    setForm(prev => ({ ...prev, [campo]: valor }));
    setErros(prev => ({ ...prev, [campo]: undefined, geral: undefined }));
  };

  // Conferência na tela, na hora — o servidor confere de novo.
  const cpfCompleto = soDigitos(form.cpf).length === 11;
  const cpfRuim = cpfCompleto && !cpfValido(form.cpf);
  const confirmacaoCompleta = soDigitos(form.cpf_confirmacao).length === 11;
  const cpfsDiferentes = confirmacaoCompleta && soDigitos(form.cpf) !== soDigitos(form.cpf_confirmacao);
  const cpfsIguais = confirmacaoCompleta && !cpfsDiferentes && cpfValido(form.cpf);

  const validarNaTela = () => {
    const novos = {};
    if (!form.first_name.trim()) novos.first_name = 'Informe o seu nome.';
    if (!cpfValido(form.cpf)) novos.cpf = 'Este CPF não é válido. Confira os números.';
    if (soDigitos(form.cpf) !== soDigitos(form.cpf_confirmacao)) {
      novos.cpf_confirmacao = 'Os dois CPFs não são iguais. Confira e digite de novo.';
    }
    if (!form.email.trim()) novos.email = 'Informe o seu e-mail.';
    if (link?.precisa_escola && !form.school) novos.school = 'Escolha a escola em que você trabalha.';
    if (form.password.length < 8) novos.password = 'A senha precisa ter pelo menos 8 caracteres.';
    else if (form.password !== form.password_confirm) novos.password_confirm = 'As senhas não são iguais.';
    setErros(novos);
    return Object.keys(novos).length === 0;
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (!validarNaTela()) return;
    setEnviando(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/autocadastro/publico/${token}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, school: form.school ? Number(form.school) : null }),
      });
      const dados = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setEnviado(true);
        return;
      }
      const recebidos = {};
      Object.entries(dados).forEach(([campo, msg]) => {
        recebidos[campo === 'error' || campo === 'non_field_errors' ? 'geral' : campo] = primeiraMensagem(msg);
      });
      setErros(recebidos);
    } catch {
      setErros({ geral: 'Não foi possível enviar. Verifique sua internet e tente de novo.' });
    } finally {
      setEnviando(false);
    }
  };

  const rotulo = `block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`;
  const campo = (temErro) => `w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    temErro
      ? 'border-[#E83A3C]'
      : darkMode ? 'border-slate-600/40' : 'border-slate-200'
  } ${darkMode ? 'bg-slate-700/60 text-slate-100 placeholder-slate-500' : 'bg-white text-slate-800 placeholder-slate-400'}`;
  const erroDe = (nome) => erros[nome] && (
    <p className="text-xs text-[#E83A3C] mt-1">{erros[nome]}</p>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10 sm:py-10"
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
      }}
    >
      <button
        type="button"
        onClick={() => setDarkMode(!darkMode)}
        aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
        className={`fixed top-5 right-5 w-12 h-12 rounded-xl flex items-center justify-center z-10 ${darkMode
          ? 'bg-slate-800/80 border border-slate-600/15 text-yellow-400'
          : 'bg-white/80 border border-slate-200 text-slate-600 shadow-lg'}`}
      >
        {darkMode ? '☀' : '☾'}
      </button>

      <div className="w-full max-w-[560px]">
        <div className={`relative border rounded-3xl px-6 py-8 sm:px-10 sm:py-10 overflow-hidden ${darkMode
          ? 'bg-slate-800/80 border-slate-600/15'
          : 'bg-white/90 border-slate-200 shadow-xl'}`}>
          <div className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #0974BA, #2E4974)' }} />

          <div className="text-center mb-6">
            <img src="/iconSIDEB.svg" alt="SIDEB" className="w-16 h-16 mx-auto mb-3 object-contain" />
            <div className={`text-3xl font-bold bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-slate-700 to-slate-500'}`}>
              SIDEB +
            </div>
            {link && !enviado && (
              <p className={`text-sm mt-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Cadastro de <strong>{link.role_display}</strong> — {link.city_name}
              </p>
            )}
          </div>

          {carregando && (
            <p className={`text-center text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Carregando...
            </p>
          )}

          {!carregando && erroLink && (
            <div className="text-center">
              <h2 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                Não foi possível abrir este cadastro
              </h2>
              <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{erroLink}</p>
              <Link to="/login" className="text-sm text-[#0974BA] hover:underline">Ir para o login</Link>
            </div>
          )}

          {enviado && (
            <div className="text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center bg-[#13983D]/15 text-[#13983D] text-3xl">
                ✓
              </div>
              <h2 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                Recebemos seu cadastro
              </h2>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                A coordenação vai conferir seus dados. Você receberá um e-mail em{' '}
                <strong>{form.email}</strong> quando o acesso for liberado — aí é só
                entrar com o seu CPF e a senha que você criou.
              </p>
            </div>
          )}

          {!carregando && link && !enviado && (
            <form onSubmit={enviar} noValidate className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={rotulo} htmlFor="first_name">Nome *</label>
                  <input id="first_name" className={campo(erros.first_name)} value={form.first_name}
                    onChange={e => mudar('first_name', e.target.value)} autoComplete="given-name" />
                  {erroDe('first_name')}
                </div>
                <div>
                  <label className={rotulo} htmlFor="last_name">Sobrenome</label>
                  <input id="last_name" className={campo(erros.last_name)} value={form.last_name}
                    onChange={e => mudar('last_name', e.target.value)} autoComplete="family-name" />
                  {erroDe('last_name')}
                </div>
              </div>

              <div className={`rounded-2xl p-4 border ${darkMode
                ? 'bg-[#0974BA]/10 border-[#0974BA]/25'
                : 'bg-[#0974BA]/5 border-[#0974BA]/20'}`}>
                <p className={`text-xs mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  O CPF é o dado mais importante do cadastro: é com ele que você entra no
                  sistema. Digite duas vezes para conferir.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={rotulo} htmlFor="cpf">CPF *</label>
                    <input id="cpf" inputMode="numeric" placeholder="000.000.000-00"
                      className={campo(erros.cpf || cpfRuim)} value={form.cpf}
                      onChange={e => mudar('cpf', mascararCpf(e.target.value))} />
                    {erroDe('cpf') || (cpfRuim && (
                      <p className="text-xs text-[#E83A3C] mt-1">Este CPF não é válido. Confira os números.</p>
                    ))}
                  </div>
                  <div>
                    <label className={rotulo} htmlFor="cpf_confirmacao">Confirme o CPF *</label>
                    <input id="cpf_confirmacao" inputMode="numeric" placeholder="000.000.000-00"
                      className={campo(erros.cpf_confirmacao || cpfsDiferentes)} value={form.cpf_confirmacao}
                      // Colar o mesmo texto não confere nada: a segunda vez
                      // tem de ser digitada.
                      onPaste={e => e.preventDefault()}
                      onChange={e => mudar('cpf_confirmacao', mascararCpf(e.target.value))} />
                    {erroDe('cpf_confirmacao') || (cpfsDiferentes && (
                      <p className="text-xs text-[#E83A3C] mt-1">Os dois CPFs não são iguais.</p>
                    )) || (cpfsIguais && (
                      <p className="text-xs text-[#13983D] mt-1">✓ CPFs conferem</p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={rotulo} htmlFor="email">E-mail *</label>
                  <input id="email" type="email" className={campo(erros.email)} value={form.email}
                    onChange={e => mudar('email', e.target.value)} autoComplete="email" />
                  {erroDe('email')}
                </div>
                <div>
                  <label className={rotulo} htmlFor="phone">Telefone (WhatsApp)</label>
                  <input id="phone" type="tel" className={campo(erros.phone)} value={form.phone}
                    placeholder="(83) 99999-0000"
                    onChange={e => mudar('phone', e.target.value)} autoComplete="tel" />
                  {erroDe('phone')}
                </div>
              </div>

              {link.precisa_escola && (
                <div>
                  <label className={rotulo} htmlFor="school">Escola em que você trabalha *</label>
                  <select id="school" className={campo(erros.school)} value={form.school}
                    onChange={e => mudar('school', e.target.value)}>
                    <option value="">Selecione a escola...</option>
                    {link.escolas.map(escola => (
                      <option key={escola.id} value={escola.id}>{escola.school}</option>
                    ))}
                  </select>
                  {erroDe('school')}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={rotulo} htmlFor="password">Crie uma senha *</label>
                  <input id="password" type="password" className={campo(erros.password)} value={form.password}
                    onChange={e => mudar('password', e.target.value)} autoComplete="new-password" />
                  {erroDe('password')}
                </div>
                <div>
                  <label className={rotulo} htmlFor="password_confirm">Repita a senha *</label>
                  <input id="password_confirm" type="password" className={campo(erros.password_confirm)}
                    value={form.password_confirm}
                    onChange={e => mudar('password_confirm', e.target.value)} autoComplete="new-password" />
                  {erroDe('password_confirm')}
                </div>
              </div>

              {erros.geral && (
                <p className="text-sm text-[#E83A3C] text-center">{erros.geral}</p>
              )}

              <button type="submit" disabled={enviando}
                className="w-full rounded-xl py-3 text-white font-semibold text-sm disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}>
                {enviando ? 'Enviando...' : 'Enviar cadastro'}
              </button>
              <p className={`text-xs text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                O acesso é liberado depois que a coordenação aprovar o cadastro.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Autocadastro;
